import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

export default function Settings() {
  const { role, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'Viewer' });
  const [editingUser, setEditingUser] = useState(null);

  const load = async () => {
    const { data } = await api.get('/settings');
    setSettings(data);
    if (PERMISSIONS.canManageUsers(role)) {
      const { data: u } = await api.get('/users');
      setUsers(u);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Pengaturan disimpan');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
    finally { setSaving(false); }
  };

  const openNewUser = () => { setEditingUser(null); setUserForm({ name: '', email: '', password: '', role: 'Viewer' }); setUserDialogOpen(true); };
  const openEditUser = (u) => { setEditingUser(u); setUserForm({ name: u.name, email: u.email, password: '', role: u.role }); setUserDialogOpen(true); };
  const saveUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload = { name: userForm.name, email: userForm.email, role: userForm.role };
        if (userForm.password) payload.password = userForm.password;
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success('User diperbarui');
      } else {
        await api.post('/users', userForm);
        toast.success('User dibuat');
      }
      setUserDialogOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };
  const delUser = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success('User dihapus'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };

  if (!settings) return <div className="text-muted-foreground">Memuat pengaturan…</div>;

  return (
    <div className="space-y-4" data-testid="settings-page">
      <Tabs defaultValue="company">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="company">Perusahaan</TabsTrigger>
          <TabsTrigger value="bank">Bank</TabsTrigger>
          <TabsTrigger value="tax" data-testid="settings-tab-tax">Pajak</TabsTrigger>
          <TabsTrigger value="terms">Syarat & Ketentuan</TabsTrigger>
          <TabsTrigger value="numbering">Penomoran</TabsTrigger>
          {PERMISSIONS.canManageUsers(role) && <TabsTrigger value="users">Pengguna</TabsTrigger>}
        </TabsList>

        <TabsContent value="company">
          <Card className="bg-card border-border"><CardContent className="p-5">
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Nama Perusahaan</Label><Input value={settings.company_name || ''} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={settings.company_email || ''} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Telepon</Label><Input value={settings.company_phone || ''} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} /></div>
              <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Alamat</Label><Textarea rows={2} value={settings.company_address || ''} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} /></div>
              <div className="md:col-span-2 flex justify-end"><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan</Button></div>
            </form>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="bank">
          <Card className="bg-card border-border"><CardContent className="p-5">
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Nama Bank</Label><Input value={settings.company_bank_name || ''} onChange={(e) => setSettings({ ...settings, company_bank_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Nomor Rekening</Label><Input value={settings.company_bank_account_number || ''} onChange={(e) => setSettings({ ...settings, company_bank_account_number: e.target.value })} /></div>
              <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Atas Nama</Label><Input value={settings.company_bank_account_holder || ''} onChange={(e) => setSettings({ ...settings, company_bank_account_holder: e.target.value })} /></div>
              <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Instruksi Pembayaran Default</Label><Textarea rows={3} value={settings.default_payment_instructions || ''} onChange={(e) => setSettings({ ...settings, default_payment_instructions: e.target.value })} /></div>
              <div className="md:col-span-2 flex justify-end"><Button type="submit" disabled={saving}>Simpan</Button></div>
            </form>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="tax">
          <Card className="bg-card border-border"><CardContent className="p-5">
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="settings-tax-form">
              <div className="md:col-span-2 rounded-lg border border-border bg-card/50 p-3 text-xs text-muted-foreground">
                Pengaturan ini hanya menyediakan <span className="text-foreground font-medium">default</span>. Toggle PPN dan PPh tetap diaktifkan per invoice — tidak otomatis untuk semua invoice.
              </div>
              <div className="md:col-span-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
                <div className="font-medium text-primary mb-1">Metode PPN: e-Faktur DPP Nilai Lain</div>
                <div className="text-muted-foreground">
                  PPN dihitung otomatis sesuai aturan PKP Indonesia:<br />
                  <span className="font-mono-num">DPP Nilai Lain = DPP × 11/12</span> &nbsp;•&nbsp;
                  <span className="font-mono-num">PPN = 12% × DPP Nilai Lain</span> &nbsp;•&nbsp;
                  <span className="font-mono-num">Grand Total = DPP + PPN</span>
                </div>
              </div>
              <div className="flex items-center gap-3 md:col-span-2 rounded-lg border border-border p-3">
                <input
                  type="checkbox"
                  id="company_is_pkp"
                  className="h-4 w-4"
                  checked={!!settings.company_is_pkp}
                  onChange={(e) => setSettings({ ...settings, company_is_pkp: e.target.checked })}
                />
                <Label htmlFor="company_is_pkp" className="text-sm font-medium cursor-pointer">Perusahaan adalah PKP</Label>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tanggal Efektif PKP</Label>
                <Input type="date" value={settings.pkp_effective_date ? String(settings.pkp_effective_date).slice(0, 10) : ''} onChange={(e) => setSettings({ ...settings, pkp_effective_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Default Jenis PPh</Label>
                <Select value={settings.default_withholding_tax_type || 'PPh'} onValueChange={(v) => setSettings({ ...settings, default_withholding_tax_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['PPh', 'PPh 21', 'PPh 23', 'PPh 4(2)', 'Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Default PPh Rate (%)</Label>
                <Input type="number" step="0.01" inputMode="decimal" value={settings.default_withholding_tax_rate ?? 2} onChange={(e) => setSettings({ ...settings, default_withholding_tax_rate: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Default Dasar Hitung PPh</Label>
                <Select value={settings.default_withholding_tax_base || 'dpp'} onValueChange={(v) => setSettings({ ...settings, default_withholding_tax_base: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dpp">DPP (setelah diskon)</SelectItem>
                    <SelectItem value="gross_invoice">Total Invoice (DPP + PPN)</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Default Label PPh (untuk UI)</Label>
                <Input value={settings.default_withholding_tax_label || ''} placeholder="PPh 2% dari DPP" onChange={(e) => setSettings({ ...settings, default_withholding_tax_label: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex justify-end"><Button type="submit" disabled={saving} data-testid="settings-tax-save">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan</Button></div>
            </form>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="terms">
          <Card className="bg-card border-border"><CardContent className="p-5">
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Syarat Penawaran</Label><Textarea rows={6} value={settings.quotation_terms || ''} onChange={(e) => setSettings({ ...settings, quotation_terms: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Syarat Invoice</Label><Textarea rows={6} value={settings.invoice_terms || ''} onChange={(e) => setSettings({ ...settings, invoice_terms: e.target.value })} /></div>
              <div className="md:col-span-2 flex justify-end"><Button type="submit" disabled={saving}>Simpan</Button></div>
            </form>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="numbering">
          <Card className="bg-card border-border"><CardContent className="p-5">
            <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Prefix Client</Label><Input value={settings.prefix_client || ''} onChange={(e) => setSettings({ ...settings, prefix_client: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Prefix Penawaran</Label><Input value={settings.prefix_quotation || ''} onChange={(e) => setSettings({ ...settings, prefix_quotation: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Prefix Invoice</Label><Input value={settings.prefix_invoice || ''} onChange={(e) => setSettings({ ...settings, prefix_invoice: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Prefix Pembayaran</Label><Input value={settings.prefix_payment || ''} onChange={(e) => setSettings({ ...settings, prefix_payment: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Prefix Piutang</Label><Input value={settings.prefix_receivable || ''} onChange={(e) => setSettings({ ...settings, prefix_receivable: e.target.value })} /></div>
              <div className="col-span-2 md:col-span-3 flex justify-end"><Button type="submit" disabled={saving}>Simpan</Button></div>
            </form>
            <div className="mt-4 text-xs text-muted-foreground">Format: PREFIX-YYYY-0001 (mis. GP-INV-2026-0001). Perubahan berlaku untuk dokumen yang dibuat selanjutnya.</div>
          </CardContent></Card>
        </TabsContent>

        {PERMISSIONS.canManageUsers(role) && (
          <TabsContent value="users">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-heading">Pengguna</CardTitle>
                <Button size="sm" onClick={openNewUser} data-testid="user-add-button"><Plus className="h-3.5 w-3.5 mr-1" />Tambah User</Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} className="border-border">
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                        <TableCell>{u.role}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditUser(u)}>Edit</Button>
                          {u.id !== user?.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-300"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Hapus user?</AlertDialogTitle><AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => delUser(u)}>Hapus</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
              <DialogContent className="max-w-md bg-card border-border">
                <DialogHeader><DialogTitle>{editingUser ? 'Edit User' : 'User Baru'}</DialogTitle></DialogHeader>
                <form onSubmit={saveUser} className="space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">Nama</Label><Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Password {editingUser ? '(kosongkan jika tidak diubah)' : ''}</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required={!editingUser} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Role</Label>
                    <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Owner">Owner</SelectItem>
                        <SelectItem value="Accounting">Accounting</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter><Button type="button" variant="ghost" onClick={() => setUserDialogOpen(false)}>Batal</Button><Button type="submit">Simpan</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
